<div class="sub-page">

  <!-- Page Header -->
  <div class="page-header">
    <div class="header-left">
      <h1 class="page-title">Subscription Management</h1>
      <p class="page-subtitle">Manage and monitor user subscriptions</p>
    </div>
    <div class="header-actions">
      <button class="btn btn-outline pagination-btn"
        style="padding: 10px 24px; font-weight: 600; display: flex; align-items: center; gap: 8px;"
        (click)="exportData()"
        [disabled]="activeTab() === 'plans' || exportCount() === 0">
        <i class="pi pi-download"></i> Export {{ exportCount() > 0 ? '(' + exportCount() + ')' : '' }}
      </button>
      <button class="create-lobby-btn" (click)="openAddModal()" [disabled]="activeTab() === 'subscriptions'">
        <i class="pi pi-plus"></i> Add Subscription
      </button>
    </div>
  </div>

  <!-- Tab Switcher -->
  <div class="tabs-container">
    <button class="tab-button" [class.active]="activeTab() === 'subscriptions'" (click)="switchTab('subscriptions')">
      <i class="pi pi-users"></i> User Subscriptions
    </button>
    <button class="tab-button" [class.active]="activeTab() === 'plans'" (click)="switchTab('plans')">
      <i class="pi pi-list"></i> Subscription Plans
    </button>
  </div>

  @if (activeTab() === 'subscriptions') {
    <!-- Filters -->
    <div class="filters-card">
      <div class="filters-row">
        <div class="filter-group search-group">
          <label class="filter-label">Search Users</label>
          <app-search-box tooltipText="Search By firstName, lastName, and email" [value]="searchTerm" (search)="onSearch($event)" />
        </div>

        <!-- Subscription Type filter -->
        <div class="filter-group">
          <label class="filter-label">Subscription Type</label>
          <select class="filter-select" [(ngModel)]="subscriptionType">
            <option [ngValue]="null">All Types</option>
            @for (plan of plansList(); track plan) {
              <option [value]="plan.subscriptionType">
                {{ plan.name }}
              </option>
            }
          </select>
        </div>

        <!-- Subscription Status filter (API-level) -->
        <div class="filter-group">
          <label class="filter-label">Subscription Status</label>
          <select class="filter-select" [(ngModel)]="subscriptionStatusFilter">
            <option [ngValue]="null">All Statuses</option>
            <option value="active">Active</option>
            <option value="cancelled">Cancelled</option>
            <option value="expired">Expired</option>
            <option value="free">Free</option>
          </select>
        </div>

        <button class="btn-apply-filters" (click)="applyFilters()">
          <i class="pi pi-filter"></i> Apply Filters
        </button>
      </div>
    </div>

    <!-- Table -->
    <div class="table-card">
      <table class="sub-table">
        <thead>
          <tr>
            <th style="width: 48px; text-align: center;">
              <input type="checkbox" [checked]="allSelected()" (change)="toggleSelectAll()" style="width: 16px; height: 16px; cursor: pointer;" />
            </th>
            <th class="sortable" (click)="onSort('user')">
              User <span class="sort-indicator" [class.active]="currentSortField() === 'user'">{{ getSortIcon('user') }}</span>
            </th>
            <th class="sortable" (click)="onSort('plan')">
              Plan <span class="sort-indicator" [class.active]="currentSortField() === 'plan'">{{ getSortIcon('plan') }}</span>
            </th>
            <th class="sortable" (click)="onSort('status')">
              Status <span class="sort-indicator" [class.active]="currentSortField() === 'status'">{{ getSortIcon('status') }}</span>
            </th>
            <th class="sortable" (click)="onSort('date')">
              Date <span class="sort-indicator" [class.active]="currentSortField() === 'date'">{{ getSortIcon('date') }}</span>
            </th>
            <th class="text-start">Actions</th>
          </tr>
        </thead>
        <tbody>
          @if (loader()) {
            <tr>
              <td colspan="6" class="loader-cell">
                <app-spinner-loader [width]="35" />
              </td>
            </tr>
          } @else if (subscriptions().length === 0) {
            <tr>
              <td colspan="6" class="empty-cell">No subscriptions found</td>
            </tr>
          } @else {
            @for (sub of subscriptions(); track sub.id) {
              <tr>
                <td style="text-align: center;">
                  <input type="checkbox" [checked]="selectedSubIds().has(sub.id)" (change)="toggleSelectSub(sub.id)" style="width: 16px; height: 16px; cursor: pointer;" />
                </td>
                <td>
                  <div class="user-cell">
                    <div class="user-avatar" [style.background]="sub.user.avatarBg">
                      @if (sub.user.avatar) {
                        <img [src]="sub.user.avatar" [alt]="sub.user.name" class="avatar-img"
                          (error)="sub.user.avatar = undefined" />
                      }
                      @if (!sub.user.avatar) {
                        <span>{{ sub.user.initials }}</span>
                      }
                    </div>
                    <div class="user-info">
                      <p class="user-name">{{ sub.user.name | titlecase}}</p>
                      <p class="user-email">{{ sub.user.email }}</p>
                    </div>
                  </div>
                </td>
                <td>
                  <span class="plan-badge" [ngClass]="getPlanClass(sub.plan)">
                    {{ sub.plan }}
                  </span>
                </td>
                <td>
                  <span class="status-badge" [ngClass]="sub.isActive ? 'status-active' : 'status-expired'">
                    {{ sub.isActive ? 'Active' : 'Inactive' }}
                  </span>
                </td>
                <td class="date-cell">{{ sub.date }}</td>
                <td>
                  <div class="action-btns">
                    <button class="btn btn-view" title="View details" (click)="viewSubscription(sub)">View</button>
                    <!-- <button class="action-btn cancel-btn" title="Cancel subscription" [disabled]="sub.status === 'Cancelled'"
                    (click)="cancelSubscription(sub)">
                    <i class="pi pi-times"></i>
                  </button> -->
                </div>
              </td>
            </tr>
          }
        }
      </tbody>
    </table>

    <!-- Pagination Section -->
    <div class="pagination-section">
      <div class="pagination-info">
        <span>
          Showing {{ startIndex }} to {{ endIndex }} of
          {{ totalCount() }} Entries
        </span>
      </div>
      <div class="d-flex gap-2">
        @if (subscriptions().length !== 0) {
          <label class="page-size-label" for="pageSizeSelect">Page Size:</label>
          <select id="pageSizeSelect" [(ngModel)]="pageSize" (ngModelChange)="onPageSizeChange($event)"
            class="form-select">
            @for (size of pageSizes; track size) {
              <option [value]="size">
                {{ size }}
              </option>
            }
          </select>
        }
        <button class="btn btn-outline pagination-btn"
          [disabled]="currentPage === 1 || loader() || subscriptions().length === 0" (click)="previousPage()">
          &lt; Prev
        </button>
        <button class="btn btn-outline pagination-btn" [disabled]="currentPage === totalPages || loader()"
          (click)="nextPage()">
          Next &gt;
        </button>
      </div>
    </div>
  </div>
}

@if (activeTab() === 'plans') {
  <!-- Plans Table -->
  <div class="table-card">
    <table class="sub-table">
      <thead>
        <tr>
          <th class="sortable" (click)="onSortPlans('name')">
            Plan Name <span class="sort-indicator" [class.active]="planSortField() === 'name'">{{ getSortPlanIcon('name') }}</span>
          </th>
          <th class="sortable" (click)="onSortPlans('appStoreProductId')">
            App ID <span class="sort-indicator" [class.active]="planSortField() === 'appStoreProductId'">{{ getSortPlanIcon('appStoreProductId') }}</span>
          </th>
          <th class="sortable" (click)="onSortPlans('subscriptionType')">
            Type <span class="sort-indicator" [class.active]="planSortField() === 'subscriptionType'">{{ getSortPlanIcon('subscriptionType') }}</span>
          </th>
          <th class="sortable" (click)="onSortPlans('duration')">
            Duration <span class="sort-indicator" [class.active]="planSortField() === 'duration'">{{ getSortPlanIcon('duration') }}</span>
          </th>
          <th class="sortable" (click)="onSortPlans('price')">
            Price <span class="sort-indicator" [class.active]="planSortField() === 'price'">{{ getSortPlanIcon('price') }}</span>
          </th>
          <th>Features</th>
          <th class="sortable" (click)="onSortPlans('isActive')">
            Status <span class="sort-indicator" [class.active]="planSortField() === 'isActive'">{{ getSortPlanIcon('isActive') }}</span>
          </th>
          <th class="text-center">Actions</th>
        </tr>
      </thead>
      <tbody>
        @if (loader()) {
          <tr>
            <td colspan="8" class="loader-cell">
              <app-spinner-loader [width]="35" />
            </td>
          </tr>
        } @else if (plansList().length === 0) {
          <tr>
            <td colspan="8" class="empty-cell">No subscription plans found</td>
          </tr>
        } @else {
          @for (plan of plansList(); track plan._id) {
            <tr>
              <td style="font-weight: 600; color: #1a1a2e;">{{ plan.name }}</td>
              <td>
                <div class="store-ids">
                  <p class="store-id" style="margin: 0; font-size: 0.85rem;"><i class="pi pi-apple text-xs"></i> {{
                plan.appStoreProductId || 'N/A' }}</p>
                <p class="store-id" style="margin: 0; font-size: 0.85rem;"><i class="pi pi-android text-xs"></i> {{
              plan.playStoreProductId || 'N/A' }}</p>
            </div>
          </td>
          <td style="text-transform: capitalize;">{{ plan.subscriptionType }}</td>
          <td>{{ plan.duration }} days</td>
          <td style="font-weight: 600; color: #c2185b;">{{ plan.price | currency: plan.currency || 'USD' }}</td>
          <td>
            <div class="features-list d-flex flex-wrap gap-1">
              @for (feat of plan.features; track feat) {
                <span class="badge bg-secondary"
                style="font-size: 0.75rem; padding: 4px 8px; border-radius: 4px;">{{ feat }}</span>
              }
            </div>
          </td>
          <td>
            <span class="status-badge" [ngClass]="plan.isActive ? 'status-active' : 'status-expired'">
              {{ plan.isActive ? 'Active' : 'Inactive' }}
            </span>
          </td>
          <td class="text-center">
            <div class="action-btns" style="justify-content: flex-end;">
              <button class="btn btn-view" title="View details" (click)="viewPlan(plan)">View</button>
            </div>
          </td>
        </tr>
      }
    }
  </tbody>
</table>
</div>
}

</div>

<!-- Add User Subscription Modal -->
@if (showAddModal()) {
  <app-add-subscription-modal [visible]="showAddModal()" [plans]="plans" (onClose)="closeAddModal()"
    (onSave)="onSubscriptionAdded($event)" />
  }

  <!-- View User Subscription Modal -->
  @if (showViewModal() && selectedSubscription()) {
    <app-view-subscription-modal [visible]="showViewModal()" [subscription]="selectedSubscription()!"
      (onClose)="closeViewModal()" />
    }

    <!-- View Subscription Plan Modal -->
    @if (showViewPlanModal() && selectedPlanDetails()) {
      <app-view-subscription-plan-modal [visible]="showViewPlanModal()" [plan]="selectedPlanDetails()!"
        (onClose)="closeViewPlanModal()" (onSave)="loadPlans()" />
      }